/**
 * Typed browser fetch client for the same-origin /api/v1/* proxy.
 * Automatic single-flight 401 refresh; credentials always included.
 * Adapted (trimmed) from accio-smm/frontend/lib/api.ts.
 */

import type {
  Activity,
  BoardColumn,
  BoardData,
  Client,
  ClientPage,
  Component,
  Contact,
  ContactDetail,
  Dashboard,
  PipelineStage,
  Project,
  Settings,
  Stage,
  User,
} from "./types";

const BASE_URL = "/api/v1";

export const SESSION_EXPIRED_EVENT = "auth:session-expired";

export class ApiError extends Error {
  status: number;
  data: Record<string, unknown> | null;
  constructor(status: number, data: Record<string, unknown> | null) {
    super(String(data?.error ?? data?.message ?? `Request failed (${status})`));
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

let isRefreshing = false;
let waiters: Array<{ resolve: () => void; reject: (e: unknown) => void }> = [];
function drainWaiters(error: unknown | null) {
  waiters.forEach((w) => (error ? w.reject(error) : w.resolve()));
  waiters = [];
}

async function parseError(r: Response): Promise<Record<string, unknown> | null> {
  return (await r.json().catch(() => null)) as Record<string, unknown> | null;
}

async function request<T>(path: string, options: RequestInit = {}, isRetry = false): Promise<T> {
  const response = await fetch(`${BASE_URL}${path}`, {
    credentials: "include",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(options.headers as Record<string, string> | undefined),
    },
  });

  if (response.ok) {
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  }

  if (response.status === 401 && !isRetry) {
    if (path === "/auth/refresh") throw new ApiError(401, await parseError(response));
    if (isRefreshing) {
      await new Promise<void>((resolve, reject) => waiters.push({ resolve, reject }));
      return request<T>(path, options, true);
    }
    isRefreshing = true;
    try {
      const refresh = await fetch(`${BASE_URL}/auth/refresh`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      if (!refresh.ok) throw new Error("refresh failed");
      drainWaiters(null);
      return request<T>(path, options, true);
    } catch (err) {
      drainWaiters(err);
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
      }
      throw new ApiError(401, await parseError(response));
    } finally {
      isRefreshing = false;
    }
  }

  throw new ApiError(response.status, await parseError(response));
}

function toQuery(params?: Record<string, string | number | boolean | undefined | null>): string {
  if (!params) return "";
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null) sp.append(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export const api = {
  get: <T>(path: string, params?: Record<string, string | number | boolean | undefined | null>) =>
    request<T>(`${path}${toQuery(params)}`, { method: "GET" }),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body: body !== undefined ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PUT", body: body !== undefined ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PATCH", body: body !== undefined ? JSON.stringify(body) : undefined }),
  del: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};

// ─── Endpoint helpers ─────────────────────────────────────────────

export const authApi = {
  login: (email: string, password: string) =>
    api.post<{ message: string; user: User }>("/auth/login", { email, password }),
  me: () => api.get<User>("/auth/me"),
  logout: () => api.post<{ message: string }>("/auth/logout"),
};

export const componentsApi = {
  list: (activeOnly = false) =>
    api.get<{ components: Component[] }>("/components", activeOnly ? { active_only: "true" } : undefined),
  create: (body: Partial<Component>) => api.post<Component>("/components", body),
  update: (id: number, body: Partial<Component>) => api.put<Component>(`/components/${id}`, body),
  remove: (id: number) => api.del<{ deleted: number }>(`/components/${id}`),
  reorder: (order: number[]) => api.post<{ reordered: number }>("/components/reorder", { order }),
};

export interface SaveVariantInput {
  name?: string;
  mode: string;
  two_way?: boolean;
  entrances: number;
  exits: number;
  notes?: string;
  intro_text?: string;
  scope_text?: string;
  items: Array<{
    component_id: number | null;
    name: string;
    model: string;
    manufacturer: string;
    unit: string;
    qty: number;
    cost_price: number;
    margin_percent: number;
  }>;
}

export interface ContactInput {
  first_name: string;
  last_name: string;
  details: ContactDetail[];
  is_primary?: boolean;
}

// Clients (companies/projects). Each client holds many offer variants and many
// contact persons.
export const clientsApi = {
  list: (company?: string) =>
    api.get<{ clients: Client[] }>("/clients", company ? { company } : undefined),
  // Pipeline board: summary + first page of every column.
  board: (company?: string) => api.get<BoardData>("/clients/board", company ? { company } : undefined),
  // One more page of a single column (infinite scroll).
  boardColumn: (stage: Stage, cursor: number | null, company?: string) =>
    api.get<BoardColumn>("/clients/board", { stage, cursor, company }),
  // Flat paginated list (list view infinite scroll).
  page: (opts: { stage?: Stage | null; cursor?: number | null; company?: string }) =>
    api.get<ClientPage>("/clients/page", { stage: opts.stage, cursor: opts.cursor, company: opts.company }),
  get: (id: number) =>
    api.get<{ client: Client; variants: Project[] }>(`/clients/${id}`),
  create: (body: { company: string; object_name?: string; bin?: string; contact?: ContactInput }) =>
    api.post<Client>("/clients", body),
  update: (id: number, body: { company: string; object_name?: string; bin?: string }) =>
    api.put<Client>(`/clients/${id}`, body),
  remove: (id: number) => api.del<{ deleted: number }>(`/clients/${id}`),
  createVariant: (clientId: number, body?: { name?: string }) =>
    api.post<Project>(`/clients/${clientId}/variants`, body ?? {}),
  setStage: (id: number, body: { stage: Stage; lost_reason?: string }) =>
    api.patch<Client>(`/clients/${id}/stage`, body),
  setActiveVariant: (id: number, variantId: number | null) =>
    api.patch<Client>(`/clients/${id}`, { active_variant_id: variantId }),
};

export interface ActivityInput {
  type?: "note" | "call" | "meeting" | "task";
  title?: string;
  body?: string;
  due_at?: string | null;
  done?: boolean;
}

// Timeline entries / follow-up tasks under a client.
export const activitiesApi = {
  list: (clientId: number) => api.get<{ activities: Activity[] }>(`/clients/${clientId}/activities`),
  create: (clientId: number, body: ActivityInput) => api.post<Activity>(`/clients/${clientId}/activities`, body),
  update: (clientId: number, id: number, body: ActivityInput) =>
    api.patch<Activity>(`/clients/${clientId}/activities/${id}`, body),
  remove: (clientId: number, id: number) => api.del<{ deleted: number }>(`/clients/${clientId}/activities/${id}`),
};

// Contact persons under a client.
export const contactsApi = {
  create: (clientId: number, body: ContactInput) =>
    api.post<Contact>(`/clients/${clientId}/contacts`, body),
  update: (clientId: number, contactId: number, body: ContactInput) =>
    api.put<Contact>(`/clients/${clientId}/contacts/${contactId}`, body),
  remove: (clientId: number, contactId: number) =>
    api.del<{ deleted: number }>(`/clients/${clientId}/contacts/${contactId}`),
};

// A variant/offer (the calculation the editor works on).
export const variantsApi = {
  get: (id: number) => api.get<{ variant: Project; has_offer: boolean }>(`/variants/${id}`),
  update: (id: number, body: SaveVariantInput) => api.put<Project>(`/variants/${id}`, body),
  remove: (id: number) => api.del<{ deleted: number }>(`/variants/${id}`),
};

export const dashboardApi = {
  get: () => api.get<Dashboard>("/dashboard"),
};

export interface StageInput {
  label: string;
  color: string;
  is_start?: boolean;
  is_end?: boolean;
  is_won?: boolean;
}

export const stagesApi = {
  list: () => api.get<{ stages: PipelineStage[] }>("/pipeline/stages"),
  create: (body: StageInput) => api.post<PipelineStage>("/pipeline/stages", body),
  update: (id: number, body: StageInput) => api.put<PipelineStage>(`/pipeline/stages/${id}`, body),
  remove: (id: number) => api.del<{ deleted: number; reassigned_to: string }>(`/pipeline/stages/${id}`),
  reorder: (order: number[]) => api.post<{ reordered: number }>("/pipeline/stages/reorder", { order }),
};

export const settingsApi = {
  get: () => api.get<Settings>("/settings"),
  update: (body: Partial<Settings>) => api.put<Settings>("/settings", body),
};

export const usersApi = {
  list: () => api.get<{ users: User[] }>("/users"),
  create: (body: {
    email: string;
    first_name: string;
    last_name: string;
    phone?: string;
    role: string;
    password: string;
  }) => api.post<User>("/users", body),
  update: (
    id: number,
    body: Partial<{
      email: string;
      first_name: string;
      last_name: string;
      phone: string;
      role: string;
      password: string;
    }>,
  ) => api.put<User>(`/users/${id}`, body),
  remove: (id: number) => api.del<{ deleted: number }>(`/users/${id}`),
};

export const profileApi = {
  update: (body: {
    first_name: string;
    last_name: string;
    phone: string;
    current_password?: string;
    new_password?: string;
  }) => api.put<User>("/profile", body),
};
